/**
 * Breadcrumb Utility Functions
 * 
 * These utilities help map URL paths to breadcrumb trails and vice versa.
 * Used for navigation synchronization and clickable breadcrumbs.
 */

/**
 * Builds a comprehensive mapping from URL paths to breadcrumb information
 * 
 * @param {Array} menuData - Raw menu data from API (client_schema_name[2])
 * @returns {Object} pathMapping - Map of URL path to breadcrumb info
 * 
 * Example output:
 * {
 *   "/employee_registration": {
 *     breadcrumb: "Admin > Employee > New Employee",
 *     levels: [
 *       { name: "Admin", path: null },
 *       { name: "Employee", path: null },
 *       { name: "New Employee", path: "/employee_registration" }
 *     ]
 *   }
 * }
 */
export const buildPathToBreadcrumbMapping = (menuData) => {
  if (!menuData || !Array.isArray(menuData)) return {};

  const pathMapping = {};
  
  // First pass: collect all paths for each level combination
  // This helps find if a parent level has its own navigable path
  const levelPaths = {};
  
  menuData.forEach((item) => {
    const { LVL1, LVL2, LVL3, ui_path } = item;
    
    if (ui_path) {
      // Store path for LVL1 > LVL2 combination (when LVL3 is empty)
      if (LVL1 && LVL2 && !LVL3) {
        const key = `${LVL1}|${LVL2}`;
        levelPaths[key] = ui_path;
      }
      
      // Store path for LVL1 only (if exists)
      if (LVL1 && !LVL2 && !LVL3) {
        levelPaths[LVL1] = ui_path;
      }
    }
  });

  // Second pass: build complete breadcrumb mapping
  menuData.forEach((item) => {
    const { LVL1, LVL2, LVL3, ui_path, ui_element, screen_id } = item;
    
    if (!ui_path) return;
    
    const levels = [];
    const breadcrumbParts = [];
    
    // Level 1
    if (LVL1) {
      breadcrumbParts.push(LVL1);
      levels.push({
        name: LVL1,
        path: levelPaths[LVL1] || null, // Path if LVL1 is directly navigable
        level: 1
      });
    }
    
    // Level 2
    if (LVL2) {
      breadcrumbParts.push(LVL2);
      const lvl2Key = `${LVL1}|${LVL2}`;
      levels.push({
        name: LVL2,
        path: levelPaths[lvl2Key] || null, // Path if LVL2 is directly navigable
        level: 2
      });
    }
    
    // Level 3
    if (LVL3) {
      breadcrumbParts.push(LVL3);
      levels.push({
        name: LVL3,
        path: ui_path,
        level: 3
      });
    } else if (LVL2) {
      // If no LVL3, the LVL2 item itself is the destination
      // Update the last level's path
      levels[levels.length - 1].path = ui_path;
    } else if (LVL1) {
      // If only LVL1, it's the destination
      levels[levels.length - 1].path = ui_path;
    }
    
    pathMapping[ui_path] = {
      breadcrumb: breadcrumbParts.join(" > "),
      levels: levels,
      ui_element: ui_element,
      screen_id: screen_id
    };
  });

  return pathMapping;
};

/**
 * Builds a reverse mapping from breadcrumb text to URL path
 * Useful for finding navigation path from breadcrumb segment
 * 
 * @param {Array} menuData - Raw menu data from API
 * @returns {Object} Map of "LVL1 > LVL2 > LVL3" to URL path
 */
export const buildBreadcrumbToPathMapping = (menuData) => {
  if (!menuData || !Array.isArray(menuData)) return {};

  const breadcrumbToPath = {};
  
  menuData.forEach((item) => {
    const { LVL1, LVL2, LVL3, ui_path } = item;
    
    if (!ui_path) return;
    
    const parts = [LVL1, LVL2, LVL3].filter(Boolean);
    const breadcrumb = parts.join(" > ");
    
    breadcrumbToPath[breadcrumb] = ui_path;
    
    // Also store partial paths for parent navigation
    // e.g., "Admin > Employee" should map to its path if it has one
    if (LVL1 && LVL2 && !LVL3) {
      breadcrumbToPath[`${LVL1} > ${LVL2}`] = ui_path;
    }
    if (LVL1 && !LVL2 && !LVL3) {
      breadcrumbToPath[LVL1] = ui_path;
    }
  });

  return breadcrumbToPath;
};

/**
 * Gets breadcrumb info for a given URL path
 * 
 * @param {string} urlPath - Current URL path (e.g., "/employee_registration")
 * @param {Object} pathMapping - The mapping object from buildPathToBreadcrumbMapping
 * @returns {Object|null} Breadcrumb info or null if not found
 */
export const getBreadcrumbForPath = (urlPath, pathMapping) => {
  if (!urlPath || !pathMapping) return null;
  
  // Handle root path
  if (urlPath === "/" || urlPath === "") {
    return {
      breadcrumb: "Home",
      levels: [{ name: "Home", path: "/", level: 0 }]
    };
  }
  
  return pathMapping[urlPath] || null;
};

/**
 * Parses a breadcrumb string into clickable segments with paths
 * 
 * @param {string} breadcrumbString - e.g., "Admin > Employee > New Employee"
 * @param {Object} pathMapping - The path mapping object
 * @param {Array} menuData - Raw menu data for finding parent paths
 * @returns {Array} Array of { name, path, isClickable }
 */
export const parseBreadcrumbToSegments = (breadcrumbString, pathMapping, menuData) => {
  if (!breadcrumbString || breadcrumbString === "Home") {
    return [{ name: "Home", path: "/", isClickable: true, isLast: true }];
  }

  const segments = breadcrumbString.split(" > ").map(s => s.trim());
  const result = [];
  
  // Build breadcrumb-to-path lookup
  const breadcrumbToPath = buildBreadcrumbToPathMapping(menuData || []);
  
  segments.forEach((segment, index) => {
    const isLast = index === segments.length - 1;
    
    // Build partial breadcrumb up to this segment
    const partialBreadcrumb = segments.slice(0, index + 1).join(" > ");
    
    // Find if this partial path has a navigable route
    const path = breadcrumbToPath[partialBreadcrumb] || null;
    
    result.push({
      name: segment,
      path: path,
      isClickable: !!path && !isLast, // Clickable if has path and not the current page
      isLast: isLast
    });
  });

  return result;
};

/**
 * Stores breadcrumb state in sessionStorage (survives refresh, cleared on tab close)
 * Using sessionStorage instead of localStorage to handle refresh better
 */
export const storeBreadcrumbState = (urlPath, breadcrumbInfo) => {
  try {
    const state = {
      urlPath,
      breadcrumb: breadcrumbInfo?.breadcrumb || "Home",
      timestamp: Date.now()
    };
    sessionStorage.setItem("breadcrumbState", JSON.stringify(state));
  } catch (e) {
    console.warn("Failed to store breadcrumb state:", e);
  }
};

/**
 * Retrieves stored breadcrumb state
 */
export const getStoredBreadcrumbState = () => {
  try {
    const stored = sessionStorage.getItem("breadcrumbState");
    return stored ? JSON.parse(stored) : null;
  } catch (e) {
    console.warn("Failed to retrieve breadcrumb state:", e);
    return null;
  }
};

export default {
  buildPathToBreadcrumbMapping,
  buildBreadcrumbToPathMapping,
  getBreadcrumbForPath,
  parseBreadcrumbToSegments,
  storeBreadcrumbState,
  getStoredBreadcrumbState
};
